import type {FirebaseOptions} from 'firebase/app';
import {features} from '#common/features';
import type {LogLevel} from '#common/logging/logger';

type Environment = 'development' | 'local' | 'production' | 'test';

type LocaleSource = 'bundle' | 'esmsh' | 'jsdelivr';

export const environment: Environment =
  import.meta.env.MODE === 'production' ? 'production' : import.meta.env.MODE === 'test' ? 'test' : 'development';

export interface SessionExtensionConfiguration {
  /**
   * If > 0, allows the user to extend the session by this many minutes.
   * Keep this in sync with sessionExtensionPeriodMinutes in firestore.rules
   */
  extensionPeriodMinutes: number;

  /**
   * How long before a session expires should the session extension modal be displayed?
   */
  warningPeriodMinutes: number;
}

export interface SessionExpirationConfiguration {
  /**
   * Automatically logs the user out this many minutes after logging in
   * unless the session is extended (see sessionExtensionPeriodMinutes).
   * Keep this in sync with maximumSessionLengthMinutes in firestore.rules
   */
  maximumSessionLengthMinutes: number;

  /**
   * Configure to allow sessions to be extended beyond the initial expiration time.
   */
  sessionExtension?: SessionExtensionConfiguration;
}

interface FirebaseRemoteConfiguration {
  fetchTimeoutMillis: number;
  minimumFetchIntervalMillis: number;
}

interface AuthenticationConfiguration {
  google: {
    /**
     * The sign-in method to use for Google.
     * Before setting this to redirect, please read the following documentation:
     * https://firebase.google.com/docs/auth/web/redirect-best-practices
     */
    signInMethod: 'popup' | 'redirect';
  };
}

export interface Configuration {
  authentication: AuthenticationConfiguration;
  environment: Environment;
  firebase: FirebaseOptions;
  firebaseRemoteConfiguration: FirebaseRemoteConfiguration;
  localeSource: LocaleSource;
  logLevel: LogLevel;
  logStartupTimings?: boolean;
  sessionExpiration?: SessionExpirationConfiguration;
}

type OptionalConfigurations = Pick<
  Configuration,
  'authentication' | 'firebaseRemoteConfiguration' | 'localeSource' | 'logStartupTimings'
>;

export const ConfigurationDefaults: OptionalConfigurations = Object.freeze({
  authentication: {
    google: {
      signInMethod: 'popup',
    },
  },
  firebaseRemoteConfiguration: {
    fetchTimeoutMillis: 2_500,
    minimumFetchIntervalMillis: 60_000,
  },
  localeSource: 'jsdelivr',
} satisfies OptionalConfigurations);

const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.'));

const getAuthDomain = (defaultAuthDomain: string): string => {
  if (typeof window !== 'undefined' && !isLocalhost && window.location.hostname) {
    return window.location.hostname;
  }
  return defaultAuthDomain;
};

const configurations: Record<Environment, Configuration> = Object.freeze({
  development: {
    ...ConfigurationDefaults,
    authentication: {
      google: {
        signInMethod: isLocalhost ? 'popup' : 'redirect',
      },
    },
    environment: 'development',
    firebase: {
      apiKey: 'AIzaSyAJRfzprHRxcwtNkZzrjtrWKg3YB3yTFMk',
      appId: '1:887181067095:web:f537c6b6c4bd37655db412',
      authDomain: getAuthDomain('jbaldassari-firebase-quikstart.firebaseapp.com'),
      messagingSenderId: '887181067095',
      projectId: 'jbaldassari-firebase-quikstart',
      storageBucket: 'jbaldassari-firebase-quikstart.appspot.com',
    },
    logLevel: 'debug',
    logStartupTimings: true,
    sessionExpiration: features.sessionExpiration
      ? {
          maximumSessionLengthMinutes: 5,
          sessionExtension: {
            extensionPeriodMinutes: 5,
            warningPeriodMinutes: 4,
          },
        }
      : undefined,
  } satisfies Configuration,
  local: {
    ...ConfigurationDefaults,
    environment: 'development',
    firebase: {
      apiKey: 'AIzaSyAJRfzprHRxcwtNkZzrjtrWKg3YB3yTFMk',
      appId: '1:887181067095:web:f537c6b6c4bd37655db412',
      authDomain: 'jbaldassari-firebase-quikstart.firebaseapp.com',
      messagingSenderId: '887181067095',
      projectId: 'jbaldassari-firebase-quikstart',
      storageBucket: 'jbaldassari-firebase-quikstart.appspot.com',
    },
    logLevel: 'debug',
    logStartupTimings: true,
    sessionExpiration: features.sessionExpiration
      ? {
          maximumSessionLengthMinutes: 5,
          sessionExtension: {
            extensionPeriodMinutes: 5,
            warningPeriodMinutes: 4,
          },
        }
      : undefined,
  } satisfies Configuration,
  production: {
    ...ConfigurationDefaults,
    authentication: {
      google: {
        signInMethod: isLocalhost ? 'popup' : 'redirect',
      },
    },
    environment: 'production',
    firebase: {
      apiKey: 'AIzaSyAJRfzprHRxcwtNkZzrjtrWKg3YB3yTFMk',
      appId: '1:887181067095:web:f537c6b6c4bd37655db412',
      authDomain: getAuthDomain('jbaldassari-firebase-quikstart.firebaseapp.com'),
      messagingSenderId: '887181067095',
      projectId: 'jbaldassari-firebase-quikstart',
      storageBucket: 'jbaldassari-firebase-quikstart.appspot.com',
    },
    logLevel: 'error',
    sessionExpiration: features.sessionExpiration
      ? {
          maximumSessionLengthMinutes: 60,
          sessionExtension: {
            extensionPeriodMinutes: 30,
            warningPeriodMinutes: 10,
          },
        }
      : undefined,
  } satisfies Configuration,
  test: undefined as unknown as Configuration, // Note: See mockConfig.ts for the test environment configuration
});

export const configuration = Object.freeze(configurations[environment]);
